import type {
    AiAnalysisEntry,
} from "@mrflark/spl_service_model";

export const AI_MODEL_PRIORITY = [
    {
        pattern: "claude-opus-4-6",
        label: "Anthropic Claude Opus 4.6",
        provider: "anthropic",
    },
    {
        pattern: "claude-opus-4-5",
        label: "Anthropic Claude Opus 4.5",
        provider: "anthropic",
    },
    {
        pattern: "claude-sonnet-4-6",
        label: "Anthropic Claude Sonnet 4.6",
        provider: "anthropic",
    },
    {
        pattern: "claude-sonnet-4-5",
        label: "Anthropic Claude Sonnet 4.5",
        provider: "anthropic",
    },
    {
        pattern: "qwen-7b",
        label: "RTX 5080 Vision Offload Qwen 7B",
        provider: "qwen",
    },
    {
        pattern: "qwen2.5-vl-3b",
        label: "Local Inference Qwen 3B",
        provider: "qwen",
    },
] as const;

// lower index = higher priority
const ANTHROPIC_FALLBACK_PRIORITY = 4.5;
const UNKNOWN_PRIORITY = 999;

export const getModelPriority = (modelName: string): number => {
    const index = AI_MODEL_PRIORITY.findIndex(
        (entry) => modelName.includes(entry.pattern),
    );
    if (index >= 0) return index;

    // unknown anthropic models rank between last named anthropic and first local
    if (modelName.startsWith("claude-")) return ANTHROPIC_FALLBACK_PRIORITY;

    return UNKNOWN_PRIORITY;
};

export const shouldPromoteToPrimary = (
    newModelName: string,
    currentPrimaryModelName: string | null,
): boolean => {
    if (!currentPrimaryModelName) return true;
    return getModelPriority(newModelName) <
        getModelPriority(currentPrimaryModelName);
};

export type PrimaryAnalysisResult = {
    modelName: string;
    entry: AiAnalysisEntry;
};

export const getPrimaryAnalysis = (
    analyses?: Record<string, AiAnalysisEntry>,
): PrimaryAnalysisResult | null => {
    if (!analyses) return null;

    const entries = Object.entries(analyses);
    if (entries.length === 0) return null;

    // look for explicitly marked primary
    for (const [modelName, entry] of entries) {
        if (entry.is_primary) return { modelName, entry };
    }

    // fallback: highest priority model present
    let bestEntry: PrimaryAnalysisResult | null = null;
    let bestPriority = UNKNOWN_PRIORITY + 1;

    for (const [modelName, entry] of entries) {
        const priority = getModelPriority(modelName);
        if (priority < bestPriority) {
            bestPriority = priority;
            bestEntry = { modelName, entry };
        }
    }

    return bestEntry;
};

export const REANALYSIS_STEPS = [
    {
        stepId: "ai_analysis",
        label: "AI Analysis",
        description:
            "Description, suggested tags, and detected objects via the chosen vision model.",
        defaultChecked: true,
    },
    {
        stepId: "detect_watermark",
        label: "Watermark Detection",
        description:
            "Re-runs the watermark detector. Honors any prior \"no watermark\" admin verdict on the same row.",
        defaultChecked: true,
    },
    {
        stepId: "extract_ocr",
        label: "OCR Extraction",
        description:
            "Reads visible text out of the image — meme captions, labels, signage.",
        defaultChecked: true,
    },
] as const;

export type ReanalysisStepId = typeof REANALYSIS_STEPS[number]["stepId"];

// "Remote (Anthropic)" listed first so it's the default selection in
// the reanalysis modal — local qwen is on a smaller model and runs
// against an unreliable inference container; anthropic is the path
// most operators want first
export const AI_PROVIDERS = [
    {
        id: "anthropic",
        label: "Remote (Anthropic)",
        defaultModel: "claude-opus-4-7",
    },
    { id: "qwen", label: "Local Qwen", defaultModel: "qwen2.5-vl-3b" },
] as const;

export type AiProviderId = typeof AI_PROVIDERS[number]["id"];

export const ANTHROPIC_MODELS = [
    { id: "claude-opus-4-7", label: "Claude Opus 4.7" },
    { id: "claude-sonnet-4-7", label: "Claude Sonnet 4.7" },
    { id: "claude-sonnet-4-20250514", label: "Claude Sonnet 4.5" },
    { id: "claude-opus-4-20250514", label: "Claude Opus 4.5" },
    { id: "claude-haiku-4-5-20251001", label: "Claude Haiku 4.5" },
] as const;
